import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { FiatCurrency } from '@sorare/core/src/__generated__/globalTypes';
import paypalSvg from '@sorare/core/src/assets/paypal.svg';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useApplyJonumRestriction } from '@sorare/core/src/hooks/useApplyJonumRestriction.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

const Logo = styled.img`
  background: var(--c-white);
  padding: var(--half-unit);
  border-radius: var(--radius-xxs);
  height: var(--triple-unit);
`;

type Props = {
  amount: number;
  fiatCurrency: FiatCurrency;
  showLogo?: boolean;
};

const PAYPAL_PAY_IN_4_MIN_AMOUNT_CENTS = 3000;

export const usePaypalBanner = () => {
  const applyJonumRestriction = useApplyJonumRestriction();
  const {
    flags: { usePaypalBanner: usePaypalBannerFlag },
  } = useFeatureFlags();
  return usePaypalBannerFlag && !applyJonumRestriction;
};

export const PaypalBanner = ({ amount, fiatCurrency, showLogo }: Props) => {
  const { formatNumber } = useIntl();

  if (amount < PAYPAL_PAY_IN_4_MIN_AMOUNT_CENTS) return null;

  const installmentAmount = formatNumber(amount / 100 / 4, {
    style: 'currency',
    currency: fiatCurrency,
  });

  return (
    <Horizontal>
      {!!showLogo && <Logo alt="PayPal" src={paypalSvg} />}
      <LabelM>
        <FormattedMessage
          defaultMessage="4 interest-free payments of {amount}. No late fees. <a>Learn more</a>"
          values={{
            amount: installmentAmount,
            a: c => (
              <a
                className="text-link"
                href="https://www.paypal.com/digital-wallet/ways-to-pay/buy-now-pay-later"
                target="_blank"
                rel="noopener nofollow noreferrer"
              >
                {c}
              </a>
            ),
          }}
        />
      </LabelM>
    </Horizontal>
  );
};
